import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import MainLayout from '../../components/layout/MainLayout';
import { Link } from 'react-router-dom';
import traceApi from '../../services/api/trace.api';

export default function APMPage() {
  const { t } = useTranslation('apm');  const [traces, setTraces] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('1h');
  useEffect(() => {
    loadApplications();
  }, []);

  useEffect(() => {
    if (applications.length > 0 && selectedAppId) {
      loadTraces();
    }
  }, [selectedAppId, timeRange, applications]);

  const loadApplications = async () => {
    try {
      console.log('[APMPage] Loading applications...');
      const response = await applicationApi.getAll();
      console.log('[APMPage] Applications response:', response);
      if (response.success) {
        const appsWithAll = [{ id: 'all', name: t('filters.allApplications') }, ...response.data];
        setApplications(appsWithAll);
        console.log('[APMPage] Set applications:', appsWithAll);
        console.log('[APMPage] Current selectedAppId:', selectedAppId);
        // Keep the default 'all' value from useState
      }
    } catch (error) {
      console.error('Error loading applications:', error);
    }
  };

  const loadTraces = async () => {
    console.log('[APMPage] loadTraces called, selectedAppId:', selectedAppId);
    if (!selectedAppId) {
      console.log('[APMPage] No selectedAppId, returning');
      return;
    }

    try {
      setLoading(true);
      const endTime = Date.now();
      const startTime = endTime - getTimeRangeMs();

      console.log('[APMPage] Fetching traces for app:', selectedAppId, 'from', new Date(startTime), 'to', new Date(endTime));

      const [tracesResponse, statsResponse] = await Promise.all([
        traceApi.getAll(selectedAppId || 'all', { startTime, endTime, limit: 100 }),
        traceApi.getStatistics(selectedAppId || 'all', startTime, endTime)
      ]);

      console.log('[APMPage] Traces response:', tracesResponse);
      console.log('[APMPage] Stats response:', statsResponse);

      if (tracesResponse.success) {
        console.log('[APMPage] Setting traces:', tracesResponse.data.length, 'items');
        setTraces(tracesResponse.data);
      }

      if (statsResponse.success) {
        console.log('[APMPage] Setting stats:', statsResponse.data);
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('[APMPage] Error loading traces:', error);
      setTraces([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRangeMs = () => {
    const ranges: any = {
      '15m': 15 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000
    };
    return ranges[timeRange] || ranges['1h'];
  };

  const calculateMetrics = () => {
    if (traces.length === 0) return { throughput: 0, avgTime: 0, errorRate: 0, p95: 0 };

    const totalTraces = traces.length;
    const errors = traces.filter((t: any) => t.status === 'error').length;
    const avgDuration = traces.reduce((acc: number, t: any) => acc + (t.duration_ms || 0), 0) / totalTraces;
    const sortedDurations = [...traces].map((t: any) => t.duration_ms || 0).sort((a, b) => a - b);
    const p95Index = Math.floor(sortedDurations.length * 0.95);

    const timeRangeMinutes = getTimeRangeMs() / (1000 * 60);
    const throughput = timeRangeMinutes > 0 ? totalTraces / timeRangeMinutes : 0;

    return {
      throughput: isFinite(throughput) ? Math.round(throughput * 10) / 10 : 0,
      avgTime: isFinite(avgDuration) ? Math.round(avgDuration) : 0,
      errorRate: totalTraces > 0 ? ((errors / totalTraces) * 100).toFixed(1) : '0.0',
      p95: sortedDurations[p95Index] || 0
    };
  };

  const getTopTransactions = () => {
    const grouped: any = {};
    traces.forEach((trace: any) => {
      const key = trace.operation_name;
      if (!grouped[key]) {
        grouped[key] = { name: key, calls: 0, totalTime: 0, errors: 0, durations: [] };
      }
      grouped[key].calls++;
      grouped[key].totalTime += trace.duration_ms;
      grouped[key].durations.push(trace.duration_ms);
      if (trace.status === 'error') grouped[key].errors++;
    });

    return Object.values(grouped).map((t: any) => {
      const sortedDurations = t.durations.sort((a: number, b: number) => a - b);
      const p95Index = Math.floor(sortedDurations.length * 0.95);
      const p99Index = Math.floor(sortedDurations.length * 0.99);
      const avgTime = t.calls > 0 ? t.totalTime / t.calls : 0;
      return {
        name: t.name,
        calls: t.calls,
        avgTime: isFinite(avgTime) ? Math.round(avgTime) : 0,
        errorRate: t.calls > 0 ? ((t.errors / t.calls) * 100).toFixed(1) : '0.0',
        p95: sortedDurations[p95Index] || 0,
        p99: sortedDurations[p99Index] || 0
      };
    }).sort((a: any, b: any) => b.calls - a.calls).slice(0, 10);
  };

  const metrics = calculateMetrics();
  const topTransactions = getTopTransactions();

  return (
    <MainLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
        </div>
        <div className="flex items-end space-x-3">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('filters.application')}</label>
            <select value={selectedAppId} onChange={(e) => setSelectedApp(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300" disabled={loading}>
              {applications.map((app) => (<option key={app.id} value={app.id}>{app.id === 'all' ? t('filters.allApplications') : app.name}</option>))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">{t('filters.timeRange')}</label>
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <option value="15m">{t('filters.last15min')}</option>
              <option value="1h">{t('filters.lastHour')}</option>
              <option value="6h">{t('filters.last6hours')}</option>
              <option value="24h">{t('filters.last24hours')}</option>
              <option value="7d">{t('filters.last7days')}</option>
            </select>
          </div>
          <Link to="/apm/service-map" className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            {t('serviceMap.title')}
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.throughput')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{metrics.throughput}</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('overview.requestsPerMin')}</p>
                </div>
                <div className="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/20">
                  <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.avgResponseTime')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{metrics.avgTime}ms</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('overview.averageDuration')}</p>
                </div>
                <div className="rounded-lg bg-green-100 p-3 dark:bg-green-900/20">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.errorRate')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{metrics.errorRate}%</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('overview.ofAllRequests')}</p>
                </div>
                <div className="rounded-lg bg-red-100 p-3 dark:bg-red-900/20">
                  <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('overview.p95Latency')}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{metrics.p95}ms</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('overview.percentile95')}</p>
                </div>
                <div className="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/20">
                  <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-6 text-lg font-semibold text-gray-900 dark:text-white">{t('topTransactions.title')}</h3>
            {topTransactions.length === 0 ? (
              <div className="flex justify-center items-center py-12 text-gray-500 dark:text-gray-400">{t('topTransactions.noData')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="pb-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('topTransactions.table.transaction')}</th>
                      <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('topTransactions.table.calls')}</th>
                      <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('topTransactions.table.avgTime')}</th>
                      <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('topTransactions.table.errorRate')}</th>
                      <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('topTransactions.table.p95')}</th>
                      <th className="pb-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('topTransactions.table.p99')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {topTransactions.map((transaction: any, i: number) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-4 text-sm font-medium text-gray-900 dark:text-white">{transaction.name}</td>
                        <td className="py-4 text-right text-sm text-gray-500 dark:text-gray-400">{transaction.calls.toLocaleString()}</td>
                        <td className="py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">{transaction.avgTime}ms</td>
                        <td className="py-4 text-right text-sm text-red-600 dark:text-red-400">{transaction.errorRate}%</td>
                        <td className="py-4 text-right text-sm text-gray-500 dark:text-gray-400">{transaction.p95}ms</td>
                        <td className="py-4 text-right text-sm text-gray-500 dark:text-gray-400">{transaction.p99}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </MainLayout>
  );
}
